import React, { useState, useEffect, useCallback } from 'react';
import { TrendingUp, TrendingDown, Loader2 } from 'lucide-react';
import axios from 'axios';

const API_V2 = process.env.REACT_APP_BACKEND_URL + '/api/v2';

const fmtPrice = (price) => {
  if (!price || price === 0) return '$0.00';
  if (price >= 100) return `$${price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  if (price >= 1) return `$${price.toFixed(4)}`;
  if (price >= 0.001) return `$${price.toFixed(6)}`;
  return `$${price.toFixed(8)}`;
};

const Sparkline = ({ data, color, change }) => {
  if (!data || data.length < 2) return <div className="w-24 h-8" />;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const w = 96, h = 32;
  const points = data.map((v, i) => {
    const x = (i / (data.length - 1)) * w;
    const y = h - ((v - min) / range) * h;
    return `${x},${y}`;
  }).join(' ');
  const lineColor = change >= 0 ? '#22c55e' : '#ef4444';
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="flex-shrink-0">
      <polyline fill="none" stroke={lineColor} strokeWidth="1.5" points={points} />
    </svg>
  );
};

const COIN_ICONS = {
  BTC: '/coins/btc.svg',
  ETH: '/coins/eth.svg',
};

const ALL_COINS = [
  { pair: 'BTC-USD', symbol: 'BTC', name: 'Bitcoin', color: '#F7931A' },
  { pair: 'ETH-USD', symbol: 'ETH', name: 'Ethereum', color: '#627EEA' },
  { pair: 'SOL-USD', symbol: 'SOL', name: 'Solana', color: '#9945FF' },
  { pair: 'LTC-USD', symbol: 'LTC', name: 'Litecoin', color: '#345D9D' },
  { pair: 'DOGE-USD', symbol: 'DOGE', name: 'Dogecoin', color: '#C2A633' },
  { pair: 'BCH-USD', symbol: 'BCH', name: 'Bitcoin Cash', color: '#8DC351' },
  { pair: 'XRP-USD', symbol: 'XRP', name: 'XRP', color: '#23292F' },
  { pair: 'SHIB-USD', symbol: 'SHIB', name: 'Shiba Inu', color: '#FF2D00' },
];

const CoinOverviewTable = ({ activePairs, onPairToggle, selectable = false, compact = false }) => {
  const [coins, setCoins] = useState(ALL_COINS.map(c => ({ ...c, price: 0, change_24h: 0, sparkline: [] })));
  const [loading, setLoading] = useState(true);

  const fetchCoins = useCallback(async () => {
    try {
      const res = await axios.get(`${API_V2}/market/coins-overview`, { withCredentials: true });
      if (res.data?.coins?.length > 0) {
        setCoins(res.data.coins);
      }
    } catch (err) {
      console.error('Failed to fetch coins overview:', err);
      // Keep showing fallback coin list with no price data
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCoins(); }, [fetchCoins]);

  // Auto-refresh every 30s
  useEffect(() => {
    const interval = setInterval(fetchCoins, 15000);
    return () => clearInterval(interval);
  }, [fetchCoins]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-6">
        <Loader2 className="h-5 w-5 text-[#00FF41] animate-spin" />
      </div>
    );
  }

  return (
    <div className="w-full overflow-x-auto" data-testid="coin-overview-table">
      <table className="w-full text-xs">
        <thead>
          <tr className="text-gray-500 uppercase tracking-wider border-b border-[#003B00]/50">
            {selectable && <th className="text-left py-2 px-2 w-8"></th>}
            <th className="text-left py-2 px-2">Asset</th>
            <th className="text-right py-2 px-2">Price</th>
            <th className="text-right py-2 px-2">24h %</th>
            <th className="text-right py-2 px-2 hidden sm:table-cell">24h Chart</th>
          </tr>
        </thead>
        <tbody>
          {coins.map((coin) => {
            const isActive = activePairs?.includes(coin.pair);
            const changeColor = coin.change_24h >= 0 ? 'text-green-400' : 'text-red-400';
            return (
              <tr
                key={coin.pair}
                className={`border-b border-[#003B00]/30 transition-colors ${
                  selectable ? 'cursor-pointer hover:bg-[#001a00]' : ''
                } ${isActive ? 'bg-[#001a00]/60' : ''}`}
                onClick={() => selectable && onPairToggle?.(coin.pair)}
                data-testid={`coin-row-${coin.symbol}`}
              >
                {selectable && (
                  <td className="py-2 px-2">
                    <div className={`w-4 h-4 border rounded-sm flex items-center justify-center ${
                      isActive ? 'border-[#00FF41] bg-[#00FF41]/20' : 'border-gray-600'
                    }`}>
                      {isActive && <div className="w-2 h-2 bg-[#00FF41] rounded-sm" />}
                    </div>
                  </td>
                )}
                <td className="py-2 px-2">
                  <div className="flex items-center gap-2">
                    <div
                      className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold text-black flex-shrink-0"
                      style={{ backgroundColor: coin.color }}
                    >
                      {coin.symbol.slice(0, 2)}
                    </div>
                    <div>
                      <div className="text-[#00FF41] font-semibold">{coin.symbol}</div>
                      {!compact && <div className="text-gray-600 text-[10px]">{coin.name}</div>}
                    </div>
                  </div>
                </td>
                <td className="py-2 px-2 text-right">
                  <span className="text-[#00FF41] font-mono">{fmtPrice(coin.price)}</span>
                </td>
                <td className="py-2 px-2 text-right">
                  <div className={`flex items-center justify-end gap-0.5 font-mono font-bold ${changeColor}`}>
                    {coin.change_24h >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                    {coin.change_24h >= 0 ? '+' : ''}{coin.change_24h}%
                  </div>
                </td>
                <td className="py-2 px-2 text-right hidden sm:table-cell">
                  <div className="flex justify-end">
                    <Sparkline data={coin.sparkline} color={coin.color} change={coin.change_24h} />
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default CoinOverviewTable;
